/* eslint-disable max-lines */
import { useUpdateTenantConfig } from '@hakimo-ui/hakimo/data-access';
import { AdminTenantDetails } from '@hakimo-ui/hakimo/types';
import { toast, useCanUpdateTenant } from '@hakimo-ui/hakimo/util';
import { Alert, Button, Toggle } from '@hakimo-ui/shared/ui-base';
import { DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { ToggleConfirmationModal } from '../components/ToggleConfirmationModal';

interface Props {
  tenant: AdminTenantDetails;
  onEditClick?: () => void;
}

export function TenantInfoCard(props: Props) {
  const { tenant } = props;
  const supportUserEmail = tenant.supportUserEmail || '---';
  const displayProductType =
    tenant.productType === 'Motion' ? 'Remote Guarding' : tenant.productType;
  const isRemoteGuarding = tenant.productType === 'Remote Guarding';
  const skipMLProcessing = tenant.config?.skipMLProcessing ?? false;
  const [showSkipMLModal, setShowSkipMLModal] = useState(false);
  const disableMediaDownload = tenant.config?.disableMediaDownload ?? false;
  const canUpdateTenant = useCanUpdateTenant();

  const updateTenantConfigMutation = useUpdateTenantConfig(
    tenant.id,
    () => {
      toast('Tenant config updated successfully', { type: 'success' });
    },
    (error: Error) => {
      toast(`Failed to update config: ${error.message}`, { type: 'error' });
    }
  );

  const handleSkipMLToggle = () => {
    if (updateTenantConfigMutation.isLoading) {
      return;
    }
    setShowSkipMLModal(true);
  };

  const handleDisableMediaDownloadToggle = () => {
    if (updateTenantConfigMutation.isLoading) {
      return;
    }
    updateTenantConfigMutation.mutate({
      config: {
        disableMediaDownload: !disableMediaDownload,
      },
    });
  };

  const handleConfirmSkipML = () => {
    updateTenantConfigMutation.mutate({
      config: {
        skipMLProcessing: !skipMLProcessing,
      },
    });
    setShowSkipMLModal(false);
  };

  const handleCopy = async () => {
    if (!tenant.supportUserEmail) {
      toast('No support user email to copy', { type: 'error' });
      return;
    }
    try {
      await navigator.clipboard.writeText(tenant.supportUserEmail);
      toast('Support user email copied to clipboard', { type: 'success' });
    } catch {
      toast('Failed to copy to clipboard', { type: 'error' });
    }
  };

  return (
    <>
      <div className="dark:bg-ondark-bg-1 bg-onlight-bg-1 overflow-hidden rounded-xl">
        <div className="px-6 py-6">
          <div className="space-y-4">
            <div>
              <dt className="text-onlight-text-2 dark:text-ondark-text-2 text-sm">
                Name
              </dt>
              <dd className="text-onlight-text-1 dark:text-ondark-text-1 mt-1 text-sm font-semibold">
                {tenant.name}
              </dd>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <dt className="text-onlight-text-2 dark:text-ondark-text-2 text-sm">
                  ID
                </dt>
                <dd className="text-onlight-text-1 dark:text-ondark-text-1 mt-1 text-sm font-semibold">
                  {tenant.id}
                </dd>
              </div>
              <div>
                <dt className="text-onlight-text-2 dark:text-ondark-text-2 text-sm">
                  Product Type
                </dt>
                <dd className="text-onlight-text-1 dark:text-ondark-text-1 mt-1 text-sm font-semibold">
                  {displayProductType}
                </dd>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <dt className="text-onlight-text-2 dark:text-ondark-text-2 text-sm">
                    Support User Email
                  </dt>
                  <dd className="text-onlight-text-1 dark:text-ondark-text-1 mt-1 text-sm font-semibold">
                    {supportUserEmail}
                  </dd>
                </div>
                {tenant.supportUserEmail && (
                  <Button
                    variant="icon"
                    onClick={handleCopy}
                    title="Copy to clipboard"
                    className="bg-onlight-bg-2 dark:bg-ondark-bg-2 !p-1"
                  >
                    <DocumentDuplicateIcon className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {tenant.supportUserEmail && (
                <div className="mt-2">
                  <Alert type="info" size="small">
                    Password for this support user email is available in 1Pass.
                  </Alert>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-6 border-t border-gray-200 pt-4 dark:border-gray-800">
              {isRemoteGuarding && (
                <div className="flex flex-col gap-y-4">
                  <dt className="text-onlight-text-2 dark:text-ondark-text-2 text-sm font-medium">
                    Skip ML Processing
                  </dt>
                  {canUpdateTenant ? (
                    <Toggle
                      enabled={skipMLProcessing}
                      onChange={handleSkipMLToggle}
                    />
                  ) : (
                    <dd className="text-onlight-text-1 dark:text-ondark-text-1 text-sm font-semibold">
                      {skipMLProcessing ? 'Enabled' : 'Disabled'}
                    </dd>
                  )}
                </div>
              )}

              <div className="flex flex-col gap-y-4">
                <dt className="text-onlight-text-2 dark:text-ondark-text-2 text-sm font-medium">
                  Disable Media Download
                </dt>
                {canUpdateTenant ? (
                  <Toggle
                    enabled={disableMediaDownload}
                    onChange={handleDisableMediaDownloadToggle}
                    infoText="Saves this preference on the tenant. It does not block media downloads yet — the operator UI still allows downloads until enforcement ships."
                  />
                ) : (
                  <dd className="text-onlight-text-1 dark:text-ondark-text-1 text-sm font-semibold">
                    {disableMediaDownload ? 'Enabled' : 'Disabled'}
                  </dd>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="dark:bg-ondark-bg-1 bg-onlight-bg-1 overflow-hidden rounded-xl">
        <div className="px-6 py-6">
          <div className="grid grid-cols-3 gap-6">
            <div>
              <dt className="text-onlight-text-2 dark:text-ondark-text-2 text-sm">
                Location
              </dt>
              <dd className="text-onlight-text-1 dark:text-ondark-text-1 mt-1 text-sm font-semibold">
                {tenant.locationCount}
              </dd>
            </div>
            <div>
              <dt className="text-onlight-text-2 dark:text-ondark-text-2 text-sm">
                Camera
              </dt>
              <dd className="text-onlight-text-1 dark:text-ondark-text-1 mt-1 text-sm font-semibold">
                {tenant.cameraCount}
              </dd>
            </div>
            <div>
              <dt className="text-onlight-text-2 dark:text-ondark-text-2 text-sm">
                Audio Devices
              </dt>
              <dd className="text-onlight-text-1 dark:text-ondark-text-1 mt-1 text-sm font-semibold">
                {tenant.audioDeviceCount}
              </dd>
            </div>
          </div>
        </div>
      </div>

      <ToggleConfirmationModal
        open={showSkipMLModal}
        title={
          skipMLProcessing
            ? 'Disable Skip ML Processing'
            : 'Enable Skip ML Processing'
        }
        message={
          <>
            <span className="text-onlight-text-2 dark:text-ondark-text-2 mb-2 block text-xs">
              {skipMLProcessing
                ? `Both Hakimo's ML service and AI Operator will run for this tenant.`
                : `Only Hakimo's ML service will be skipped for this tenant. AI Operator will continue to run normally.`}
            </span>
            {`Do you want to ${
              skipMLProcessing ? 'disable' : 'enable'
            } Skip ML Processing?`}
          </>
        }
        onConfirm={handleConfirmSkipML}
        onCancel={() => setShowSkipMLModal(false)}
        isLoading={updateTenantConfigMutation.isLoading}
      />
    </>
  );
}

export default TenantInfoCard;
